import { formatPercent } from '@solum/format'
import type { ChartValueFormatting, TimeGrain } from '@solum/experience'

/**
 * How a chart SPEAKS its values and its time buckets — the one place both
 * `InsightChart`'s axes and `TimelineChart`'s data table read from, so the
 * picture and its accessible table cannot say the same bucket two ways.
 *
 * Kit-internal: not on the barrel. A host formats its own copy through
 * `@solum/format`; this is only the chart's translation of the governed
 * `format` / `timeGrain` it was handed.
 */

/**
 * A time bucket as a person reads it (`Sep 15, 2026`, `Sep 2026`, `2026`).
 *
 * This was `TimelineChart`'s private `formatBucket`, used for its table only —
 * the chart beside that table printed the raw `2026-09-15T00:00:00` ISO key on
 * its axis (design-sync kit finding "TimelineChart x-axis shows raw ISO
 * buckets"). Moved here unchanged so both readings use it.
 *
 * A bucket is a WALL-CLOCK label in the zone the aggregate was computed in
 * (`startLocal`), so it is formatted in UTC after pinning a zone-less value to
 * UTC: formatting it in the reader's zone would shift a Brussels midnight
 * bucket onto the previous day for a reader west of Greenwich. A date-only key
 * gets an explicit midnight, because `YYYY-MM-DDZ` is a V8 leniency, not ISO.
 * Anything that does not parse (a `Not reported` group) is returned as-is.
 */
export function formatTimeBucket(value: string, grain: TimeGrain): string {
  const pinned = /^\d{4}-\d{2}-\d{2}$/u.test(value)
    ? `${value}T00:00:00Z`
    : value.endsWith('Z') || /[+-]\d\d:\d\d$/u.test(value) ? value : `${value}Z`
  const date = new Date(pinned)
  if (Number.isNaN(date.getTime())) return value
  const options: Intl.DateTimeFormatOptions = grain === 'hour'
    ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
    : grain === 'year' ? { year: 'numeric' }
      : grain === 'month' || grain === 'quarter' ? { month: 'short', year: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' }
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: 'UTC' }).format(date)
}

/**
 * The governed `definition.format`, as a number formatter for axis ticks and
 * tooltip values. `undefined` format keeps ECharts' own plain numbers.
 *
 * - `currency` — the ISO code the definition carries, locale-placed by Intl.
 *   With no `precision`, whole amounts print without `.00` (an axis of
 *   `€0.00 / €50,000.00` is noise) while a fractional one keeps its cents.
 * - `percent` — the fleet stores a percent as a WHOLE NUMBER (#1706 ruling
 *   52108), so `42` is `42%`, never `4,200%`: this goes through the shared
 *   `formatPercent`, not `Intl`'s `style: 'percent'`, which multiplies by 100.
 * - `duration` — the grammar names no unit, so none is invented: the number is
 *   grouped and rounded like `number`. Saying "42 min" for a value the source
 *   measured in hours would be worse than saying 42.
 */
export function chartValueFormatter(
  format: ChartValueFormatting | undefined,
): ((value: number) => string) | undefined {
  if (!format) return undefined
  const digits = format.precision === undefined
    ? {}
    : { minimumFractionDigits: format.precision, maximumFractionDigits: format.precision }
  if (format.kind === 'currency' && format.currency) {
    const money = new Intl.NumberFormat(undefined, {
      style: 'currency', currency: format.currency, minimumFractionDigits: 0, ...digits,
    })
    return (value) => money.format(value)
  }
  const plain = new Intl.NumberFormat(undefined, digits)
  if (format.kind === 'percent') {
    return (value) => formatPercent(format.precision === undefined
      ? value
      : Number(value.toFixed(format.precision)))
  }
  return (value) => plain.format(value)
}
